import { Injectable, inject, signal } from '@angular/core';
import { Subject } from 'rxjs';

import { SvcSignalRService } from '@solvace/core/services/signal-r/svc-signal-r.service';
import { SvcSignalRHub } from '@solvace/core/services/signal-r/svc-signal-r-hub.enum';

/**
 * App-wide notification channel: "an action plan was created, updated, deleted,
 * escalated, rescheduled, approved, rejected, reopened or cancelled — any view
 * showing action plans (search list, kanban, calendar) should refresh".
 *
 * Why a service instead of `dialogRef.close({ hadChanges })`:
 *   - Several modal actions (escalate, reschedule, approve, reject, cancel,
 *     reopen) mutate the plan WITHOUT closing the modal. A close-only refresh
 *     misses those updates until the user finally dismisses the dialog.
 *   - Multiple consumers (table view + kanban) need the same trigger; an
 *     observable channel decouples them from the dialog lifecycle.
 *
 * Consumers should read `refreshTick()` inside an `effect()` (or pass it as a
 * `refreshKey` input to the kanban) and reload their data on each new value.
 *
 * ## Caveats
 *
 * - **Singleton global** (`providedIn: 'root'`). One instance for the entire app.
 * - **Every** `SvcModalActionPlanComponent` that calls `requestRefresh()`
 *   notifies **all** consumers, regardless of where the modal was opened. Today
 *   only `actionplan-search` listens, but if other features start consuming
 *   they'll all refresh on any modal action across the app.
 * - For context-scoped refresh (e.g. only refresh the screen that opened the
 *   modal), DO NOT plug into this service — create a feature-scoped service
 *   with `providedIn` pointing at a route/component instead.
 */
@Injectable({ providedIn: 'root' })
export class SvcActionPlanRefreshService {
  readonly #signalR = inject(SvcSignalRService);
  readonly #tick = signal(0);

  /** Read-only signal — increments whenever a refresh is requested. */
  readonly refreshTick = this.#tick.asReadonly();

  public readonly rowTotalsUpdated$ = new Subject<{ referenceId: number }>();
  public readonly rowBookmarkUpdated$ = new Subject<{ referenceId: number; isFavorite: boolean }>();

  /** Notify all listeners that they should refresh action-plan-related views. */
  public requestRefresh(): void {
    this.#tick.update((value) => value + 1);
  }

  public notifyRowTotalsUpdated(referenceId: number): void {
    this.rowTotalsUpdated$.next({ referenceId });
  }

  public notifyRowBookmarkUpdated(referenceId: number, isFavorite: boolean): void {
    this.rowBookmarkUpdated$.next({ referenceId, isFavorite });
  }

  public isRealtimeConnected(): boolean {
    return this.#signalR.getAliveInstanceFromHub(SvcSignalRHub.Kanban)?.isConnected ?? false;
  }
}
